import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { CircleCheck, CloudUpload, Eye, FileText, RotateCcw } from 'lucide-react';
import { useId, useRef, useState, type DragEvent } from 'react';
import { useFormContext, useWatch, type FieldValues, type Path } from 'react-hook-form';
import { z } from 'zod';

import { choiceOptions, columnsFor, enumLabels, type EnumLike } from '@/shared/patterns';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';
import { Spinner } from '@/shared/ui/spinner';

import { CollectionField, type CollectionTexts } from './collection-field';
import { SelectFormField, TextFormField } from './fields';

/** Um documento da lista do formulário (`relatedDocs`, `uploads`): o arquivo já está no storage. */
export interface DocumentValues {
  id?: string | null | undefined;
  fileName: string;
  displayName: string;
  fileType: string | number | null;
  tempGetUrl?: string | null | undefined;
  createdAt?: string | null | undefined;
  /** Só no rascunho do sheet: o envio está em andamento (não vai ao fio). */
  uploading?: boolean | undefined;
  [field: string]: unknown;
}

/** Resultado do envio: o nome no storage (`filename` do `GET /Upload`) e o link temporário. */
export interface UploadedFile {
  readonly fileName: string;
  readonly tempGetUrl?: string | null | undefined;
}

/**
 * Envia um arquivo e só resolve quando o `PUT` terminou (X05): URL assinada e binário, pelo fio do
 * módulo. `onProgress` recebe de 0 a 1 quando o navegador informa.
 */
export type UploadFile = (file: File, options: { readonly onProgress: (fraction: number) => void; readonly signal: AbortSignal }) => Promise<UploadedFile>;

/** Extensões do upload de documentos do legado (`UploadTabContainer`). */
export const DOCUMENT_ACCEPT = 'image/*,.pdf,.doc,.docx,.xlsx,.xsl,.rar,.zip';

/** Schema do documento (P17), com as mensagens do legado (`UploadSchema.ts`, U20-5 na caixa). */
export function documentSchema(i18n: I18n): z.ZodType<DocumentValues, DocumentValues> {
  const required = (text: string) => z.string({ error: text }).trim().min(1, text);
  const fileTypeMessage = i18n._(msg`Tipo de documento: precisa ser preenchido.`);
  return z.looseObject({
    fileName: required(i18n._(msg`Upload: é necessário fazer o upload do arquivo.`)),
    displayName: required(i18n._(msg`Nome de exibição: precisa ser preenchido.`)),
    fileType: z.union([z.string(), z.number(), z.null()]).refine((value) => value !== null && value !== '', fileTypeMessage),
    uploading: z.boolean().optional(),
  });
}

const EMPTY: DocumentValues = { fileName: '', displayName: '', fileType: null, tempGetUrl: null };

type UploadState = { readonly kind: 'idle' } | { readonly kind: 'uploading'; readonly name: string; readonly progress: number | null } | { readonly kind: 'error'; readonly file: File } | { readonly kind: 'done'; readonly name: string };

function formatSize(i18n: I18n, bytes: number) {
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${new Intl.NumberFormat(i18n.locale, { maximumFractionDigits: 1 }).format(mb)} MB` : `${new Intl.NumberFormat(i18n.locale, { maximumFractionDigits: 0 }).format(Math.max(1, bytes / 1024))} KB`;
}

/**
 * Área de envio dentro de um `EntitySheet` com os campos `fileName`, `displayName` e `tempGetUrl`:
 * escolher ou arrastar; o `PUT` termina antes de o arquivo contar como enviado (X05). Serve também ao
 * "Arquivo de cessão" (um arquivo só, `accept=".docx"`).
 */
export function DocumentDrop({ upload, accept, acceptLabel }: { readonly upload: UploadFile; readonly accept: string; readonly acceptLabel: string }) {
  const { i18n, t } = useLingui();
  const inputId = useId();
  const { setValue, getValues, control } = useFormContext<DocumentValues>();
  const current = useWatch({ control, name: 'fileName' });
  const [state, setState] = useState<UploadState>({ kind: 'idle' });
  const [over, setOver] = useState(false);
  const aborter = useRef<AbortController | null>(null);

  const send = async (file: File) => {
    aborter.current?.abort();
    const controller = new AbortController();
    aborter.current = controller;
    setState({ kind: 'uploading', name: file.name, progress: null });
    setValue('uploading', true);
    try {
      const result = await upload(file, {
        signal: controller.signal,
        onProgress: (fraction) => setState({ kind: 'uploading', name: file.name, progress: Math.min(1, Math.max(0, fraction)) }),
      });
      setValue('fileName', result.fileName, { shouldValidate: true, shouldDirty: true });
      setValue('tempGetUrl', result.tempGetUrl ?? null, { shouldDirty: true });
      if (!getValues('displayName')) setValue('displayName', file.name, { shouldValidate: true, shouldDirty: true });
      setState({ kind: 'done', name: `${file.name} · ${formatSize(i18n, file.size)}` });
    } catch {
      if (!controller.signal.aborted) setState({ kind: 'error', file });
    } finally {
      setValue('uploading', false);
    }
  };

  const drop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setOver(false);
    const file = event.dataTransfer.files.item(0);
    if (file) void send(file);
  };

  const uploading = state.kind === 'uploading';
  const percent = uploading && state.progress !== null ? Math.round(state.progress * 100) : null;
  const doneName = state.kind === 'done' ? state.name : current ? current : null;

  return (
    <div className="flex flex-col gap-2">
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={drop}
        className={cn(
          'flex flex-col items-center gap-2 rounded-card border-[1.5px] border-dashed border-accent-line bg-accent-wash px-4 py-6 text-center transition-colors duration-(--lume-dur-instant)',
          over && 'border-accent',
        )}
      >
        <CloudUpload aria-hidden className="size-8 text-accent-text" />
        <label htmlFor={inputId} className="cursor-pointer text-sm font-medium text-foreground">
          {t`Arraste o arquivo ou`} <span className="text-accent-text underline underline-offset-4">{t`escolha no computador`}</span>
        </label>
        <input
          id={inputId}
          type="file"
          accept={accept}
          aria-label={t`Arquivo`}
          className="sr-only"
          disabled={uploading}
          onChange={(event) => {
            const file = event.target.files?.item(0);
            event.target.value = '';
            if (file) void send(file);
          }}
        />
        <span className="text-xs text-foreground">{acceptLabel}</span>
      </div>
      {uploading ? (
        <div className="flex flex-col gap-1.5 rounded-control border border-border bg-card p-3" aria-live="polite">
          <div className="flex items-center gap-2 text-sm">
            <Spinner className="size-4 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate">{state.name}</span>
            <span className="font-mono text-xs text-muted-foreground">{percent === null ? t`Enviando…` : `${String(percent)}%`}</span>
          </div>
          <div
            role="progressbar"
            aria-label={t`Envio do arquivo`}
            aria-valuemin={0}
            aria-valuemax={100}
            {...(percent === null ? {} : { 'aria-valuenow': percent })}
            className="h-1.5 overflow-hidden rounded-full bg-muted"
          >
            <div className={cn('h-full rounded-full bg-accent', percent === null && 'w-1/3 animate-pulse')} style={percent === null ? undefined : { width: `${String(percent)}%` }} />
          </div>
        </div>
      ) : null}
      {state.kind === 'error' ? (
        <Alert
          tone="err"
          title={t`Não foi possível enviar o arquivo`}
          action={
            <Button size="sm" variant="secondary" onClick={() => void send(state.file)}>
              <RotateCcw aria-hidden />
              {t`Tentar de novo`}
            </Button>
          }
        >
          {t`Confira a conexão e tente de novo. O documento só entra na lista depois do envio.`}
        </Alert>
      ) : null}
      {!uploading && state.kind !== 'error' && doneName ? (
        <div className="flex items-center gap-3 rounded-control border border-border bg-card p-3">
          <span className="inline-flex size-9 items-center justify-center rounded-lg bg-accent-wash text-accent-text">
            <FileText aria-hidden className="size-5" />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-medium">{doneName}</span>
            <span className="text-xs text-muted-foreground">{t`enviado`}</span>
          </span>
          <CircleCheck aria-hidden className="size-5 text-ok" />
        </div>
      ) : null}
    </div>
  );
}

export interface DocumentsFieldProps<V extends FieldValues> {
  readonly name: Path<V>;
  /** Tipos de documento (o `GET /Enum/FileType` do módulo). */
  readonly fileTypes: EnumLike;
  readonly upload: UploadFile;
  readonly onRemove?: (item: DocumentValues) => Promise<void>;
  readonly accept?: string;
  readonly acceptLabel?: string;
  /** Nome do sheet na URL. */
  readonly sheet?: string;
  readonly texts?: Partial<CollectionTexts<DocumentValues>>;
  readonly readOnly?: boolean;
}

/**
 * Aba Documentos (P17): lista, enviar (com progresso), editar e remover com confirmação (U20-3). O
 * arquivo vai direto ao storage pela URL assinada; a associação ao registro só vale no Salvar do pai.
 */
export function DocumentsField<V extends FieldValues>({ name, fileTypes, upload, accept = DOCUMENT_ACCEPT, acceptLabel, sheet = 'documento', texts, readOnly, onRemove }: DocumentsFieldProps<V>) {
  const { i18n, t } = useLingui();
  const col = columnsFor<DocumentValues>();
  const columns = [col.text('displayName', msg`Nome`), col.enum('fileType', msg`Tipo de documento`, enumLabels(fileTypes)), col.datetime('createdAt', msg`Enviado em`)];
  const merged: CollectionTexts<DocumentValues> = {
    title: t`Documentos`,
    count: (count) => (count === 1 ? t`1 arquivo` : t`${count} arquivos`),
    add: t`Adicionar documento`,
    emptyTitle: t`Nenhum documento encontrado`,
    emptyDescription: t`Os documentos enviados aqui só ficam no cadastro depois de salvar.`,
    sheetAddTitle: t`Enviar documento`,
    sheetEditTitle: t`Editar documento`,
    sheetDescription: t`Confira as informações antes de enviar o documento.`,
    submitAdd: t`Adicionar documento`,
    submitEdit: t`Salvar documento`,
    itemName: (item) => item.displayName,
    removeTitle: (item) => t`Remover ${item.displayName}?`,
    removeConfirm: t`Sim, remover`,
    saved: t`Lista de documentos atualizada`,
    removed: t`Documento removido da lista`,
    ...texts,
  };
  return (
    <CollectionField<V, DocumentValues>
      name={name}
      {...(onRemove ? { onRemove } : {})}
      texts={merged}
      columns={columns}
      getKey={(item) => item.id ?? item.fileName}
      schema={documentSchema(i18n)}
      empty={EMPTY}
      sheet={sheet}
      mobileSheet="sheet"
      canSubmit={(values) => values.uploading !== true}
      toItem={({ uploading: _uploading, ...values }) => values}
      {...(readOnly ? { readOnly } : {})}
      rowActions={[
        {
          id: 'view',
          always: true,
          icon: Eye,
          label: () => t`Abrir`,
          onSelect: (item) => {
            if (item.tempGetUrl) window.open(item.tempGetUrl, '_blank', 'noopener');
          },
        },
      ]}
      renderForm={() => (
        <>
          <DocumentDrop upload={upload} accept={accept} acceptLabel={acceptLabel ?? t`PDF, imagem, DOC, DOCX, XLSX, ZIP ou RAR`} />
          <TextFormField<DocumentValues> name="displayName" label={t`Nome de exibição`} required />
          <SelectFormField<DocumentValues> name="fileType" label={t`Tipo de documento`} required options={choiceOptions(i18n, fileTypes)} />
          <FileNameError />
        </>
      )}
    />
  );
}

/** O "Upload: é necessário…" do schema, que não tem campo próprio. */
function FileNameError() {
  const { formState } = useFormContext<DocumentValues>();
  const message = formState.errors.fileName?.message;
  return message ? (
    <p role="alert" className="text-sm text-err">
      {message}
    </p>
  ) : null;
}
